import {
  useBetaFeature,
  type CompactPlan,
  type SinglePlan,
  type Workspace
} from 'prove-common'
import borderControlIllustration from './illustrations/drop-apple-bread.svg'
import aquacultureIllustration from './illustrations/person-fish.svg'
import landAnimalsIllustration from './illustrations/person-animal-nature-2.svg'
import plantsIllustration from './illustrations/turnip-meat.svg'

export type WorkspaceId = number

export const UNASSIGNED_WORKSPACE = 'unassigned'

export type WorkspaceSelection = WorkspaceId | typeof UNASSIGNED_WORKSPACE

export type WorkspaceViewModel = Workspace & {
  illustration: string
}

export const WORKSPACE_QUERY_PARAM = 'workspace'
export const WORKSPACE_KIND_QUERY_PARAM = 'kind'
export const FAGSEKSJON = 'FAGSEKSJON'
export const TILSYNSDIVISJON = 'TILSYNSDIVISJON'

export const unassignedWorkspaceName = (kind?: string) =>
  kind === FAGSEKSJON ? 'Uten fagseksjon' : UNASSIGNED_WORKSPACE_NAME

export const unassignedWorkspaceDescription = (kind?: string) =>
  kind === FAGSEKSJON
    ? 'Planer som ikke er knyttet til en fagseksjon.'
    : UNASSIGNED_WORKSPACE_DESCRIPTION

export const WORKSPACE_BETA_FEATURE_KEY = 'workspace'

export const UNASSIGNED_WORKSPACE_NAME = 'Uten tilsynsdivisjon'

export const UNASSIGNED_WORKSPACE_DESCRIPTION =
  'Planer som ikke er knyttet til en tilsynsdivisjon.'

export const useIsWorkspaceFeatureEnabled = () =>
  useBetaFeature(WORKSPACE_BETA_FEATURE_KEY)

const workspaceIllustrations: Record<string, string> = {
  'land-animals': landAnimalsIllustration,
  'border-control': borderControlIllustration,
  'plants-feed-water': plantsIllustration,
  aquaculture: aquacultureIllustration
}

export const toWorkspaceViewModel = (
  workspace: Workspace
): WorkspaceViewModel => ({
  ...workspace,
  illustration:
    workspaceIllustrations[workspace.visualKey] ?? landAnimalsIllustration
})

export const toWorkspaceViewModels = (
  workspaces: Workspace[] | undefined
): WorkspaceViewModel[] =>
  (workspaces ?? [])
    .filter((workspace) => workspace.active)
    .toSorted((a, b) => a.sortOrder - b.sortOrder)
    .map(toWorkspaceViewModel)

export const defaultWorkspaceId = (
  workspaces: WorkspaceViewModel[]
): WorkspaceId | undefined => workspaces[0]?.id

export const isWorkspaceId = (
  value: number | undefined,
  workspaces: WorkspaceViewModel[]
): value is WorkspaceId =>
  value != null && workspaces.some((workspace) => workspace.id === value)

export const parseWorkspaceId = (
  value: string | null
): WorkspaceId | undefined => {
  if (value == null || value.trim() === '') return undefined
  const workspaceId = Number(value)
  return Number.isInteger(workspaceId) ? workspaceId : undefined
}

export const isUnassignedSelection = (
  value: WorkspaceSelection | undefined
): value is typeof UNASSIGNED_WORKSPACE => value === UNASSIGNED_WORKSPACE

export const parseWorkspaceSelection = (
  value: string | null
): WorkspaceSelection | undefined => {
  if (value === UNASSIGNED_WORKSPACE) return UNASSIGNED_WORKSPACE
  return parseWorkspaceId(value)
}

export const getWorkspaceById = (
  workspaces: WorkspaceViewModel[],
  workspaceId?: number | null
): WorkspaceViewModel | undefined =>
  workspaces.find((workspace) => workspace.id === workspaceId) ?? workspaces[0]

export const resolvePlanWorkspaceId = (
  plan: CompactPlan | SinglePlan | undefined,
  kind: string = TILSYNSDIVISJON
): WorkspaceId | undefined => {
  if (plan == null) return undefined
  if (kind === FAGSEKSJON) {
    if (plan.effectiveFagseksjonWorkspaceId != null)
      return plan.effectiveFagseksjonWorkspaceId
    if (plan.fagseksjonWorkspaceId != null) return plan.fagseksjonWorkspaceId
    const parent = plan.parents?.[0]
    return typeof parent === 'object'
      ? resolvePlanWorkspaceId(parent, kind)
      : undefined
  }
  if (plan.effectiveWorkspaceId != null) return plan.effectiveWorkspaceId
  if (plan.workspaceId) return plan.workspaceId

  const parent = plan.parents?.[0]
  if (typeof parent === 'object') {
    return resolvePlanWorkspaceId(parent)
  }

  return undefined
}

export const doesTaskBelongToPlan = (
  task: { planIds?: number[] },
  planId: number
): boolean => task.planIds?.includes(planId) === true

export const filterPlansByWorkspace = <T extends CompactPlan | SinglePlan>(
  plans: T[] | undefined,
  workspaceId: WorkspaceId
): T[] =>
  (plans ?? []).filter(
    (plan) =>
      resolvePlanWorkspaceId(plan) === workspaceId ||
      resolvePlanWorkspaceId(plan, FAGSEKSJON) === workspaceId
  )

export const collectRecursiveWorkspacePlanIds = (
  plans: CompactPlan[] | undefined,
  workspaceId: WorkspaceId
): Set<number> => {
  const planMap = new Map((plans ?? []).map((plan) => [plan.id, plan]))
  const workspacePlanIds = new Set<number>()

  const collectPlan = (planId: number) => {
    if (workspacePlanIds.has(planId)) return
    const plan = planMap.get(planId)
    if (!plan) return

    workspacePlanIds.add(planId)
    for (const childId of plan.children ?? []) {
      collectPlan(childId)
    }
  }

  for (const plan of plans ?? []) {
    if (
      plan.workspaceId === workspaceId ||
      plan.fagseksjonWorkspaceId === workspaceId
    ) {
      collectPlan(plan.id)
    }
  }

  return workspacePlanIds
}

/**
 * Plans that cannot be found under any visible workspace, i.e. plans whose
 * effective workspace is unset or points to a workspace that is not active.
 */
export const filterUnassignedPlans = <T extends CompactPlan | SinglePlan>(
  plans: T[] | undefined,
  workspaces: WorkspaceViewModel[],
  kind: string = TILSYNSDIVISJON
): T[] => {
  const activeWorkspaceIds = new Set(
    workspaces.map((workspace) => workspace.id)
  )
  return (plans ?? []).filter((plan) => {
    const workspaceId = resolvePlanWorkspaceId(plan, kind)
    return workspaceId == null || !activeWorkspaceIds.has(workspaceId)
  })
}
